/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import { FocusKeyManager } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  Input,
  OnDestroy,
  Optional,
  QueryList,
  ViewEncapsulation,
  booleanAttribute,
  numberAttribute,
} from '@angular/core';
import { merge, Observable, Subject } from 'rxjs';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import { Nullable } from '@core/types/nullable.type';
import { ChipActionDirective } from '../../directives/chip-action.directive';
import { ChipBaseComponent, ChipEvent } from '../chip-base/chip-base.component';

/**
 * Basic container component for the Chip component.
 *
 * Extended by ChipOptionGroupComponent and ChipInputGroupComponent for different interaction patterns.
 */
@Component({
  selector: 'core-chip-group-base',
  templateUrl: './chip-group-base.component.html',
  styleUrls: ['./chip-group-base.component.scss'],
  host: {
    '[class]': '"chip-group"',
    '(keydown)': 'handleKeydown($event)',
    '[attr.role]': 'role',
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipGroupBaseComponent implements AfterViewInit, OnDestroy {
  /**
   * Index of the last destroyed chip that had focus
   */
  private lastDestroyedFocusedChipIndex: Nullable<number> = null;

  /**
   * Used to manage focus within the chip list
   */
  protected keyManager!: FocusKeyManager<ChipActionDirective>;

  /**
   * Subject that emits when the component has been destroyed
   */
  protected destroyed$ = new Subject<void>();

  /**
   * Role to use if it hasn't been overwritten by the user
   */
  protected defaultRole = 'presentation';

  /**
   * Combined stream of all of the child chips' focus events
   */
  get chipFocusChanges$(): Observable<ChipEvent> {
    return this.getChipStream((chip) => chip.onFocus$);
  }

  /**
   * Combined stream of all of the child chips' destroy events
   */
  get chipDestroyedChanges$(): Observable<ChipEvent> {
    return this.getChipStream((chip) => chip.destroyed);
  }

  /**
   * Combined stream of all of the child chips' remove events
   */
  get chipRemovedChanges$(): Observable<ChipEvent> {
    return this.getChipStream((chip) => chip.removed);
  }

  /**
   * Whether the chip set is disabled
   */
  @Input({ transform: booleanAttribute })
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
    this.syncChipsState();
  }

  protected _disabled: boolean = false;

  /**
   * Whether the chip list contains chips or not
   */
  get empty(): boolean {
    return !this.chips || this.chips.length === 0;
  }

  /**
   * The ARIA role applied to the chip set
   */
  @Input()
  get role(): Nullable<string> {
    if (this.explicitRole) {
      return this.explicitRole;
    }

    return this.empty ? null : this.defaultRole;
  }

  set role(value: Nullable<string>) {
    this.explicitRole = value;
  }

  /**
   * Tabindex of the chip set
   */
  @Input({
    transform: (value: unknown) => (value == null ? 0 : numberAttribute(value)),
  })
  tabIndex: number = 0;

  private explicitRole: Nullable<string> = null;

  /**
   * Whether any of the chips inside of this chip-group-base has focus
   */
  get focused(): boolean {
    return this.hasFocusedChip();
  }

  /**
   * The chips that are part of this chip set
   */
  @ContentChildren(ChipBaseComponent, {
    // We need to use `descendants: true`, because Ivy will no longer match
    // indirect descendants if it's left as false.
    descendants: true,
  })
  chips!: QueryList<ChipBaseComponent>;

  /**
   * Flat list of all the actions contained within the chips
   */
  chipActions = new QueryList<ChipActionDirective>();

  constructor(
    protected elementRef: ElementRef<HTMLElement>,
    protected changeDetectorRef: ChangeDetectorRef,
    @Optional() private directionality: Directionality,
  ) {}

  ngAfterViewInit(): void {
    this.setUpFocusManagement();
    this.trackChipGroupBaseChanges();
    this.trackDestroyedFocusedChip();
  }

  /**
   * Checks whether any of the chips is focused
   */
  protected hasFocusedChip(): boolean {
    return this.chips && this.chips.some((chip) => chip.hasFocus());
  }

  /**
   * Syncs the chip-group-base's state with the individual chips
   */
  protected syncChipsState(): void {
    if (this.chips) {
      this.chips.forEach((chip) => {
        // eslint-disable-next-line no-param-reassign
        chip.disabled = this._disabled;
        chip.changeDetectorRef.markForCheck();
      });
    }
  }

  /**
   * Dummy method for subclasses to override. Base chip set cannot be focused
   */
  focus(): void {}

  /**
   * Handles keyboard events on the chip set
   */
  handleKeydown(event: KeyboardEvent): void {
    if (this.originatesFromChip(event)) {
      this.keyManager.onKeydown(event);
    }
  }

  /**
   * Utility to ensure all indexes are valid.
   *
   * @param index The index to be checked.
   * @returns True if the index is valid for our list of chips.
   */
  protected isValidIndex(index: number): boolean {
    return index >= 0 && index < this.chips.length;
  }

  /**
   * Removes the `tabindex` from the chip set and resets it back afterwards, allowing the
   * user to tab out of it. This prevents the set from capturing focus and redirecting
   * it back to the first chip, creating a focus trap, if it user tries to tab away.
   */
  protected allowFocusEscape(): void {
    if (this.tabIndex !== -1) {
      const previousTabIndex = this.tabIndex;
      this.tabIndex = -1;

      // Note that this needs to be a `setTimeout`, because a `Promise.resolve`
      // doesn't allow enough time for the focus to escape.
      setTimeout(() => {
        this.tabIndex = previousTabIndex;
      });
    }
  }

  /**
   * Gets a stream of events from all the chips within the set.
   * The stream will automatically incorporate any newly-added chips.
   */
  protected getChipStream<T, C extends ChipBaseComponent = ChipBaseComponent>(
    mappingFunction: (chip: C) => Observable<T>,
  ): Observable<T> {
    return this.chips.changes.pipe(
      startWith(null),
      switchMap(() => merge(...(this.chips as QueryList<C>).map(mappingFunction))),
    );
  }

  /**
   * Checks whether an event comes from inside a chip element
   */
  protected originatesFromChip(event: Event): boolean {
    let currentElement = event.target as HTMLElement | null;

    while (currentElement && currentElement !== this.elementRef.nativeElement) {
      if (currentElement.classList.contains('ui-chip')) {
        return true;
      }
      currentElement = currentElement.parentElement;
    }

    return false;
  }

  /**
   * Sets up the chip set's focus management logic
   */
  private setUpFocusManagement(): void {
    // Create a flat `QueryList` containing the actions of all of the chips.
    // This allows us to navigate both within the chip and move to the next/previous
    // one using the existing `ListKeyManager`.
    this.chips.changes.pipe(startWith(this.chips)).subscribe((chips: QueryList<ChipBaseComponent>) => {
      const actions: ChipActionDirective[] = [];
      chips.forEach((chip) => chip.getActions().forEach((action) => actions.push(action)));
      this.chipActions.reset(actions);
      this.chipActions.notifyOnChanges();
    });

    this.keyManager = new FocusKeyManager(this.chipActions)
      .withVerticalOrientation()
      .withHorizontalOrientation(this.directionality ? this.directionality.value : 'ltr')
      .withHomeAndEnd()
      .skipPredicate((action) => this.skipPredicate(action));

    // Keep the manager active index in sync so that navigation picks
    // up from the current chip if the user clicks into the list directly.
    this.chipFocusChanges$.pipe(takeUntil(this.destroyed$)).subscribe(({ chip }) => {
      const action = chip.getSourceAction(document.activeElement as Element);

      if (action) {
        this.keyManager.updateActiveItem(action);
      }
    });

    this.directionality?.change
      .pipe(takeUntil(this.destroyed$))
      .subscribe((direction) => this.keyManager.withHorizontalOrientation(direction));
  }

  /**
   * Determines if key manager should avoid putting a given chip action in the tab index. Skip
   * non-interactive and disabled actions since the user can't do anything with them.
   */
  protected skipPredicate(action: ChipActionDirective): boolean {
    // Skip chips that the user cannot interact with. `chip-group-base` does not permit focusing disabled
    // chips.
    return !action.isInteractive || action.disabled;
  }

  /**
   * Listens to changes in the chip set and syncs up the state of the individual chips
   */
  private trackChipGroupBaseChanges(): void {
    this.chips.changes.pipe(startWith(null), takeUntil(this.destroyed$)).subscribe(() => {
      if (this.disabled) {
        // Since this happens after the content has been
        // checked, we need to defer it to the next tick.
        Promise.resolve().then(() => this.syncChipsState());
      }

      this.redirectDestroyedChipFocus();
    });
  }

  /**
   * Starts tracking the destroyed chips in order to capture the focused one
   */
  private trackDestroyedFocusedChip(): void {
    this.chipDestroyedChanges$.pipe(takeUntil(this.destroyed$)).subscribe((event: ChipEvent) => {
      const chipArray = this.chips.toArray();
      const chipIndex = chipArray.indexOf(event.chip);

      // If the focused chip is destroyed, save its index so that we can move focus to the next
      // chip. We only save the index here, rather than move the focus immediately, because we want
      // to wait until the chip is removed from the chip list before focusing the next one. This
      // allows us to keep focus on the same index if the chip gets swapped out.
      if (this.isValidIndex(chipIndex) && event.chip.hasFocus()) {
        this.lastDestroyedFocusedChipIndex = chipIndex;
      }
    });
  }

  /**
   * Finds the next appropriate chip to move focus to,
   * if the currently-focused chip is destroyed.
   */
  private redirectDestroyedChipFocus(): void {
    if (this.lastDestroyedFocusedChipIndex == null) {
      return;
    }

    if (this.chips.length) {
      const newIndex = Math.min(this.lastDestroyedFocusedChipIndex, this.chips.length - 1);
      const chipToFocus = this.chips.toArray()[newIndex];

      if (chipToFocus.disabled) {
        // If we're down to one disabled chip, move focus back to the set.
        if (this.chips.length === 1) {
          this.focus();
        } else {
          this.keyManager.setPreviousItemActive();
        }
      } else {
        chipToFocus.focus();
      }
    } else {
      this.focus();
    }

    this.lastDestroyedFocusedChipIndex = null;
  }

  ngOnDestroy(): void {
    this.keyManager?.destroy();
    this.chipActions.destroy();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
